import { useEffect, useRef, useState } from "react";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";

// AddressAutocomplete — Google Places autocomplete via Supabase Edge Function proxy.
//
// Why a proxy (LESSONS L30):
// - Maps JS loader (/maps/api/js) returns 503 for the AusProp GCP project.
//   Confirmed Google-side defect; root cause unknown.
// - Places (New) REST endpoints reject browser CORS preflight, so direct
//   client calls fail with "Failed to fetch".
// - The places-proxy edge function calls Google server-side and returns a
//   normalised response with proper CORS headers.
//
// Public API (Props, ParsedAddress, onSelect signature) unchanged from previous
// versions — PropertyDetailPage doesn't need to know we swapped backends.

const DEBOUNCE_MS = 300;
const MIN_CHARS = 3;

export interface ParsedAddress {
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  latitude: number;
  longitude: number;
}

interface Props {
  onSelect: (parsed: ParsedAddress) => void;
  initialValue?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface Suggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

interface ProxyAutocompleteResponse {
  suggestions?: Suggestion[];
  error?: string;
}

interface ProxyDetailsResponse {
  addressComponents?: Array<{
    longText: string;
    shortText: string;
    types: string[];
  }>;
  location?: { latitude: number; longitude: number } | null;
  formattedAddress?: string;
  error?: string;
}

export default function AddressAutocomplete({
  onSelect,
  initialValue = "",
  className,
  placeholder = "Start typing an address…",
  disabled,
}: Props) {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session token persists across multiple autocomplete fetches + the final
  // details fetch (one Google billing session). Rotated after each selection.
  const sessionTokenRef = useRef<string>(crypto.randomUUID());
  const containerRef = useRef<HTMLDivElement>(null);
  // Race guard for debounced fetches.
  const fetchSeqRef = useRef(0);

  const supabaseReady = isSupabaseEnabled() && !!supabase;

  // Debounced autocomplete fetches via the edge function.
  useEffect(() => {
    if (!supabaseReady) {
      setError("Address search unavailable — type address manually below");
      return;
    }
    const trimmed = query.trim();
    if (trimmed.length < MIN_CHARS) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      setError(null);
      return;
    }
    const seq = ++fetchSeqRef.current;
    const handle = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: invokeErr } = await supabase!.functions.invoke<ProxyAutocompleteResponse>(
          "places-proxy",
          {
            body: {
              action: "autocomplete",
              input: trimmed,
              sessionToken: sessionTokenRef.current,
            },
          }
        );
        if (seq !== fetchSeqRef.current) return;
        if (invokeErr || data?.error) {
          console.error("[AddressAutocomplete] autocomplete failed:", invokeErr ?? data?.error);
          setError("Search failed — type address manually below");
          setSuggestions([]);
          setOpen(false);
          setLoading(false);
          return;
        }
        const parsed = data?.suggestions ?? [];
        setSuggestions(parsed);
        setOpen(true);
        setActiveIndex(parsed.length > 0 ? 0 : -1);
        setLoading(false);
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        console.error("[AddressAutocomplete] autocomplete exception:", err);
        setError("Search failed — type address manually below");
        setSuggestions([]);
        setOpen(false);
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, supabaseReady]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  async function selectSuggestion(s: Suggestion) {
    if (!supabaseReady) return;
    setOpen(false);
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase!.functions.invoke<ProxyDetailsResponse>(
        "places-proxy",
        {
          body: {
            action: "details",
            placeId: s.placeId,
            sessionToken: sessionTokenRef.current,
          },
        }
      );
      if (invokeErr || data?.error) {
        console.error("[AddressAutocomplete] details failed:", invokeErr ?? data?.error);
        setError("Couldn't fetch address details — type manually below");
        setLoading(false);
        return;
      }
      const parsed = parseAddressComponents(data ?? {});
      setLoading(false);
      // Rotate the session token for the next address pick.
      sessionTokenRef.current = crypto.randomUUID();
      if (parsed) {
        const display = s.secondaryText ? `${s.mainText}, ${s.secondaryText}` : s.mainText;
        setQuery(display);
        onSelect(parsed);
      } else {
        setError("Address details incomplete — type manually below");
      }
    } catch (err) {
      console.error("[AddressAutocomplete] details exception:", err);
      setError("Couldn't fetch address details — type manually below");
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        void selectSuggestion(suggestions[activeIndex]);
      }
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {loading && (
          <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
            <div className="h-3 w-3 rounded-full border-2 border-neutral-300 border-t-neutral-700 animate-spin" />
          </div>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-neutral-200 bg-white shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.placeId}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={(e) => { e.preventDefault(); void selectSuggestion(s); }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`cursor-pointer px-3 py-2 text-sm ${
                i === activeIndex ? "bg-blue-50" : "hover:bg-neutral-100"
              }`}
            >
              <div className="font-medium text-neutral-900">{s.mainText}</div>
              {s.secondaryText && (
                <div className="text-xs text-neutral-500">{s.secondaryText}</div>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && !loading && suggestions.length === 0 && query.trim().length >= MIN_CHARS && !error && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-500 shadow-lg">
          No matches
        </div>
      )}
      {error && <p className="mt-1 text-xs text-amber-600">{error}</p>}
    </div>
  );
}

function parseAddressComponents(data: ProxyDetailsResponse): ParsedAddress | null {
  const components = data.addressComponents ?? [];
  const location = data.location;
  if (components.length === 0 || !location) return null;
  const get = (type: string, useShort = false): string => {
    const comp = components.find((c) => c.types.includes(type));
    return comp ? (useShort ? comp.shortText : comp.longText) : "";
  };
  const streetNumber = get("street_number");
  const route = get("route");
  const subpremise = get("subpremise");
  const street = [streetNumber, route].filter(Boolean).join(" ");
  // AU slash convention for unit/apartment numbers: "8/7 Manila Place".
  const address = subpremise ? `${subpremise}/${street}` : street;
  const suburb = get("locality");
  const state = get("administrative_area_level_1", true);
  const postcode = get("postal_code");
  if (!address || !suburb || !state) return null;
  return {
    address,
    suburb,
    state,
    postcode,
    latitude: location.latitude,
    longitude: location.longitude,
  };
}
